import React, { useState, useMemo } from "react";
import { useAgentsLocal } from "./useAgentsLocal";
import {
  FiEye,        // view icon
  FiThumbsUp,   // upvote icon
  FiMessageCircle // review icon
} from "react-icons/fi";
import LexacquireTopnav from "./lexacquiretopnav";
import "./Leaderboard.css";

export default function Leaderboard() {
  const { agents: rawAgents, loading } = useAgentsLocal();

  // Normalize shape if needed (same as Acquire)
  const normalise = (raw) => {
    if (raw.name) return raw;
    return {
      id: raw.slug || raw.agent_name,
      name: raw.agent_name || "",
      description: raw.tagline || "",
      industry: raw.industry || "",
      category: raw.category || "",
      logo: raw.logo_url || "",
      url: raw.url || "",
      website_url: raw.website_url || "",
      buzz: raw.buzz || "",
      overview: raw.overview || "",
      key_features: raw.key_features || [],
      use_cases: raw.use_cases || [],
      viewCount: raw.viewCount || 0,
      voteCount: raw.voteCount || 0,
      reviewCount: raw.reviewCount || 0,
      ratingAvg: raw.ratingAvg || null,
      trendingScore: raw.trendingScore || null,
      featured: raw.featured || false,
      featuredOrder: raw.featuredOrder || 0,
      createdAt: raw.createdAt || null,
    };
  };

  // Build normalized array
  const agents = useMemo(() => rawAgents.map(normalise), [rawAgents]);

  // Compute top 10 by viewCount, voteCount, reviewCount
  const viewLeaders = useMemo(() => {
    return [...agents]
      .sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0))
      .slice(0, 10);
  }, [agents]);

  const upvoteLeaders = useMemo(() => {
    return [...agents]
      .sort((a, b) => (b.voteCount || 0) - (a.voteCount || 0))
      .slice(0, 10);
  }, [agents]);

  const reviewLeaders = useMemo(() => {
    return [...agents]
      .sort((a, b) => (b.reviewCount || 0) - (a.reviewCount || 0))
      .slice(0, 10);
  }, [agents]);

  // Local state for modal
  const [selected, setSelected] = useState(null);

  return (
    <div className="leaderboard-page">
      {/* ─── TOP NAV ─── */}
      <LexacquireTopnav />

      {loading ? (
        <p className="leaderboard-loading">Loading…</p>
      ) : (
        <>
          <h2>AI Agents Leaderboard</h2>
          <p className="leaderboard-subtitle">
            Discover the most popular and trending AI agents based on views, upvotes, and verified user reviews. Updated daily.
          </p>

          <div className="leaderboard-grid">
            {/* View Leaders Column */}
            <div className="leaderboard-column">
              <h3>View Leaders</h3>
              {viewLeaders.map((agent, idx) => {
                const rank = idx + 1;
                const rankClass = `rank-${rank}`; // for medal coloring on top 3
                return (
                  <div key={agent.id} className="leader-row">
                    <div className={`leader-rank ${rankClass}`}>{rank}</div>
                    {agent.logo ? (
                      <img
                        src={agent.logo}
                        alt={agent.name}
                        className="leader-logo clickable"
                        onClick={() => setSelected(agent)}
                      />
                    ) : (
                      <div className="leader-logo clickable" onClick={() => setSelected(agent)} />
                    )}
                    <div
                      className="leader-name clickable"
                      onClick={() => setSelected(agent)}
                    >
                      {agent.name}
                    </div>
                    <div className="leader-metric">
                      <FiEye /> { (agent.viewCount || 0).toLocaleString() }
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Upvote Leaders Column */}
            <div className="leaderboard-column">
              <h3>Upvote Leaders</h3>
              {upvoteLeaders.map((agent, idx) => {
                const rank = idx + 1;
                const rankClass = `rank-${rank}`;
                return (
                  <div key={agent.id} className="leader-row">
                    <div className={`leader-rank ${rankClass}`}>{rank}</div>
                    {agent.logo ? (
                      <img
                        src={agent.logo}
                        alt={agent.name}
                        className="leader-logo clickable"
                        onClick={() => setSelected(agent)}
                      />
                    ) : (
                      <div className="leader-logo clickable" onClick={() => setSelected(agent)} />
                    )}
                    <div
                      className="leader-name clickable"
                      onClick={() => setSelected(agent)}
                    >
                      {agent.name}
                    </div>
                    <div className="leader-metric">
                      <FiThumbsUp /> { (agent.voteCount || 0).toLocaleString() }
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Review Leaders Column */}
            <div className="leaderboard-column">
              <h3>Review Leaders</h3>
              {reviewLeaders.map((agent, idx) => {
                const rank = idx + 1;
                const rankClass = `rank-${rank}`;
                const ratingPortion = agent.ratingAvg
                  ? `⭐ ${agent.ratingAvg.toFixed(1)}`
                  : "";
                return (
                  <div key={agent.id} className="leader-row">
                    <div className={`leader-rank ${rankClass}`}>{rank}</div>
                    {agent.logo ? (
                      <img
                        src={agent.logo}
                        alt={agent.name}
                        className="leader-logo clickable"
                        onClick={() => setSelected(agent)}
                      />
                    ) : (
                      <div className="leader-logo clickable" onClick={() => setSelected(agent)} />
                    )}
                    <div
                      className="leader-name clickable"
                      onClick={() => setSelected(agent)}
                    >
                      {agent.name}
                    </div>
                    <div className="leader-metric">
                      <FiMessageCircle /> { (agent.reviewCount || 0).toLocaleString() }{" "}
                      {ratingPortion ? <span>· {ratingPortion}</span> : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* ─── DETAILS MODAL ─── */}
      {selected && (
        <div className="agent-modal" onClick={() => setSelected(null)}>
          <div
            className="agent-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="agent-modal-close"
              onClick={() => setSelected(null)}
            >
              ×
            </button>

            {selected.logo && (
              <img
                src={selected.logo}
                alt={selected.name}
                className="agent-modal-logo"
              />
            )}

            <h2>{selected.name}</h2>
            <p>{selected.description}</p>

            <p>
              <strong>Industry:</strong> {selected.industry || "—"}
            </p>
            <p>
              <strong>Category:</strong> {selected.category || "—"}
            </p>
            {selected.buzz && (
              <p>
                <strong>Buzz:</strong> {selected.buzz}
              </p>
            )}

            {selected.overview && (
              <>
                <h3>Overview</h3>
                <p>{selected.overview}</p>
              </>
            )}

            {selected.key_features.length > 0 && (
              <>
                <h3>Key Features</h3>
                <ul>
                  {selected.key_features.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              </>
            )}

            {selected.use_cases.length > 0 && (
              <>
                <h3>Use Cases</h3>
                <ul>
                  {selected.use_cases.map((u) => (
                    <li key={u}>{u}</li>
                  ))}
                </ul>
              </>
            )}

            <div className="agent-modal-links">
           
              {selected.website_url && (
                <>
                  {" • "}
                  <a
                    href={selected.website_url}
                    target="_blank"
                    rel="noreferrer"
                    className="modal-link"
                  >
                    Official Website ↗
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
